/**
 * Characters world · "Toy town": a town built from wooden toy blocks; the trail is a toy train track.
 */
import { rolePalette } from '@sketchling/core';
import type { Layer } from '../../Figure';
import { ellipse } from '../../geom';
import { cloud, rect, ridge, rng, rrect, spread } from '../kit';
import type { WorldArt } from '../types';

const palette = rolePalette({
  hillFar: '#C9D9EE',
  street: '#E7CBA6',
  lawn: '#A9D18E',
  blockRed: '#E9785B',
  blockBlue: '#5AAFD9',
  blockYellow: '#F2C14E',
  blockGreen: '#7FB86A',
  roof: '#C94F5B',
  roofBlue: '#3F7FB3',
  window: '#FFE7A6',
  door: '#8A5A3C',
  tree: '#7FB86A',
  stick: '#C98E5E',
  cloud: '#FFFFFF',
  lamp: '#FFD86B',
  balloonPink: '#F2A7C3',
  rocket: '#F4EFE6',
  harbour: '#7CC3DF',
});

function house(x: number, base: number, s: number, i: number): Layer[] {
  const bodies = ['blockRed', 'blockBlue', 'blockYellow', 'blockGreen'];
  const bw = (52 + (i % 3) * 10) * s;
  const bh = (44 + ((i * 7) % 3) * 14) * s;
  const top = base - bh;
  const out: Layer[] = [
    { k: 'region', d: rrect(x - bw / 2, top, bw, bh, 3 * s), fill: bodies[i % 4]!, shade: 7 * s, rim: true },
    { k: 'region', d: `M${x - bw / 2 - 6 * s} ${top + 1}L${x} ${top - 34 * s}L${x + bw / 2 + 6 * s} ${top + 1}Z`, fill: i % 2 ? 'roof' : 'roofBlue', shade: 5 * s },
    { k: 'region', d: rrect(x - 7 * s, base - 22 * s, 14 * s, 22 * s, 6 * s), fill: 'door', shade: false },
  ];
  for (const dx of [-bw / 4 - 2 * s, bw / 4 - 8 * s]) out.push({ k: 'region', d: rrect(x + dx - 1 * s, top + 10 * s, 12 * s, 12 * s, 2 * s), fill: 'window', shade: false });
  return out;
}

export const CHARACTERS_ART: WorldArt = {
  id: 'characters',
  palette,
  theme: {
    sky: '#DCE6F6',
    skyLow: '#FBEFD9',
    ink: '#2E3550',
    label: '#FFFBF3',
    accents: ['#E9785B', '#5AAFD9', '#F2C14E'],
    trail: { kind: 'rail', color: '#C98E5E', edge: '#8A5A3C', deco: '#9AA4B5' },
    card: { bg: '#E3EAF6', border: '#C9D5EA', shadow: '#B8C6DE' },
    node: { ring: '#E9783A', done: '#4CC38A', disc: '#FFFBF3', locked: '#DDD8CF' },
    raster: { mid: 0.68, amp: 0.07, scrim: 'rgba(255,250,240,0.16)', portrait: { mid: 0.76, amp: 0.06 } },
  },
  scene: ({ w, h, zones }) => {
    const s = h / 300;
    const R = rng(31);
    const hz = h * 0.4;
    const L: Layer[] = [];
    const z = (i: number) => zones[Math.min(i, zones.length - 1)] ?? [0, w];
    const mid = (i: number) => (z(i)[0] + z(i)[1]) / 2;
    // sky: clouds + balloons
    for (const x of spread(30, w, 240 * s, 6)) L.push(...cloud(x, hz - (60 + R() * 40) * s, 0.8 + R() * 0.4));
    for (const x of spread(120, w, 210 * s, 8)) {
      const y = hz - (70 + R() * 50) * s;
      L.push({ k: 'ink', d: `M${x} ${y + 12 * s}C${x - 3 * s} ${y + 24 * s} ${x + 3 * s} ${y + 34 * s} ${x} ${y + 46 * s}`, w: 0.45, color: 'door', taper: 'none' });
      L.push({ k: 'region', d: ellipse(x, y, 9 * s, 12 * s), fill: ['blockRed', 'blockBlue', 'blockYellow', 'balloonPink'][Math.floor(R() * 4)]!, shade: 3 * s, rim: true });
    }
    L.push({ k: 'region', d: ridge(w, hz - 10 * s, 10 * s, 220 * s, 12, h + 40), fill: 'hillFar', shade: false, line: false });
    // L2 rocket park: a toy rocket on its pad (behind the houses)
    {
      const x = mid(1) + 60 * s;
      const b = hz + 4 * s;
      L.push({ k: 'region', d: rrect(x - 26 * s, b - 8 * s, 52 * s, 10 * s, 3 * s), fill: 'stick', shade: false });
      L.push({ k: 'region', d: `M${x - 22 * s} ${b - 30 * s}L${x - 10 * s} ${b - 46 * s}L${x - 10 * s} ${b - 14 * s}Z`, fill: 'roof', shade: false });
      L.push({ k: 'region', d: `M${x + 22 * s} ${b - 30 * s}L${x + 10 * s} ${b - 46 * s}L${x + 10 * s} ${b - 14 * s}Z`, fill: 'roof', shade: false });
      L.push({ k: 'region', d: `M${x} ${b - 118 * s}C${x + 14 * s} ${b - 104 * s} ${x + 15 * s} ${b - 80 * s} ${x + 14 * s} ${b - 10 * s}L${x - 14 * s} ${b - 10 * s}C${x - 15 * s} ${b - 80 * s} ${x - 14 * s} ${b - 104 * s} ${x} ${b - 118 * s}Z`, fill: 'rocket', shade: 6 * s, rim: true });
      L.push({ k: 'region', d: ellipse(x, b - 72 * s, 7 * s), fill: 'blockBlue', shade: false });
      L.push({ k: 'region', d: `M${x - 14 * s} ${b - 98 * s}L${x + 14 * s} ${b - 98 * s}L${x + 14 * s} ${b - 92 * s}L${x - 14 * s} ${b - 92 * s}Z`, fill: 'roof', shade: false, line: false });
    }
    // houses + round trees along the back street (skip the rocket spot)
    spread(10, w, 96 * s, 14).forEach((x, i) => {
      if (Math.abs(x - (mid(1) + 60 * s)) < 50 * s) return;
      if (R() < 0.25) {
        L.push({ k: 'region', d: rect(x - 2 * s, hz + 2 * s - 34 * s, 4 * s, 34 * s), fill: 'stick', shade: false });
        L.push({ k: 'region', d: ellipse(x, hz - 40 * s, 16 * s), fill: 'tree', shade: 5 * s, rim: true });
      } else L.push(...house(x, hz + 6 * s, s, i));
    });
    L.push({ k: 'region', d: rect(-20, hz + 6 * s, w + 40, h), fill: 'lawn', shade: false, line: true });
    // L3 harbour: a strip of water with a toy sailboat (front band, below the nodes)
    {
      const [x0, x1] = z(2);
      L.push({ k: 'region', d: rrect(x0 + 10 * s, h * 0.8, x1 - x0 - 20 * s, h * 0.24, 14 * s), fill: 'harbour', shade: false });
      for (const x of spread(x0 + 30 * s, x1 - 30 * s, 50 * s, 21)) L.push({ k: 'ink', d: `M${x} ${h * 0.86}C${x + 5 * s} ${h * 0.85} ${x + 9 * s} ${h * 0.85} ${x + 14 * s} ${h * 0.86}`, w: 0.5, color: 'cloud', taper: 'both' });
      const bx = (x0 + x1) / 2 + 40 * s;
      const by = h * 0.9;
      L.push({ k: 'region', d: `M${bx - 26 * s} ${by - 8 * s}L${bx + 26 * s} ${by - 8 * s}C${bx + 20 * s} ${by + 4 * s} ${bx - 20 * s} ${by + 4 * s} ${bx - 26 * s} ${by - 8 * s}Z`, fill: 'blockRed', shade: 3 * s });
      L.push({ k: 'region', d: `M${bx - 2 * s} ${by - 12 * s}L${bx - 2 * s} ${by - 48 * s}L${bx - 24 * s} ${by - 12 * s}Z`, fill: 'cloud', shade: 2 * s });
      L.push({ k: 'region', d: `M${bx + 2 * s} ${by - 12 * s}L${bx + 2 * s} ${by - 40 * s}L${bx + 18 * s} ${by - 12 * s}Z`, fill: 'blockYellow', shade: 2 * s });
    }
    // L4 hero stadium: bunting flags across the far lawn
    {
      const [x0, x1] = z(3);
      const y = hz + 16 * s;
      L.push({ k: 'ink', d: `M${x0} ${y}C${(x0 + x1) / 2} ${y + 14 * s} ${(x0 + x1) / 2} ${y + 14 * s} ${x1} ${y}`, w: 0.5, color: 'door', taper: 'none' });
      const cols = ['blockRed', 'blockBlue', 'blockYellow', 'blockGreen'];
      spread(x0 + 10 * s, x1 - 10 * s, 26 * s, 25, 0).forEach((x, i) => {
        const t = (x - x0) / Math.max(1, x1 - x0);
        const yy = y + 4 * t * (1 - t) * 10.5 * s;
        L.push({ k: 'region', d: `M${x - 6 * s} ${yy}L${x + 6 * s} ${yy}L${x} ${yy + 12 * s}Z`, fill: cols[i % 4]!, shade: false });
      });
    }
    // L1 toy square: scattered toy blocks in the foreground
    for (const x of spread(z(0)[0] + 20 * s, z(0)[1], 70 * s, 18)) {
      const y = h * (0.86 + R() * 0.1);
      const c = ['blockRed', 'blockBlue', 'blockYellow', 'blockGreen'][Math.floor(R() * 4)]!;
      L.push({ k: 'region', d: rrect(x - 9 * s, y - 16 * s, 18 * s, 16 * s, 2 * s), fill: c, shade: 3 * s, rim: true });
    }
    L.push({ k: 'region', d: rect(-20, h * 0.96, w + 40, h), fill: 'street', shade: false, line: true });
    for (const x of spread(80, w, 300 * s, 16)) {
      if (x > z(2)[0] && x < z(2)[1]) continue;
      L.push({ k: 'region', d: rect(x - 2 * s, h * 0.74, 4 * s, h * 0.22), fill: 'roofBlue', shade: false });
      L.push({ k: 'region', d: ellipse(x, h * 0.73, 8 * s), fill: 'lamp', shade: false });
    }
    return { vb: [w, h], layers: L, guides: [] };
  },
};
